import React, { useState, useEffect } from 'react';
import menuItems from './menuList';
import { FaThumbtack } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './Menu.css';
import { ToastConfirm } from '../common/toast';
import MenuLogo from './MenuLogo';
import tenantStorage from '../../utils/tenantStorage';

const MENU_PINNED_KEY = 'rt-menu-pinned';

const Menu = ({ activePath }) => {
  // Set initial pinned state based on screen width
  const getInitialPinned = () => window.innerWidth > 768;
  const [pinned, setPinned] = useState(() => {
    // Read from localStorage on first render
    const stored = tenantStorage.getItem(MENU_PINNED_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const path = activePath || location.pathname;

  // Filter menu items based on user role
  useEffect(() => {
    const userRole = tenantStorage.getItem('userRole') || 'RT_NoAccess';
    const accessibleMenuItems = menuItems.filter(item => {
      // Check if user role is in the item's access_roles array
      return item.access_roles && item.access_roles.includes(userRole);
    });
    setFilteredMenuItems(accessibleMenuItems);
  }, []);

  const handleMenuClick = (item) => {
    if (!item.enabled) return;
    if (item.key === 'logout') {
      setShowLogoutConfirm(true);
    } else {
      navigate(item.path);
    }
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    tenantStorage.clear();
    navigate('/login', { state: { mode: 'SESSION_LOGGED_OUT' } });
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const handlePinClick = () => setPinned(p => !p);

  // Unpin menu on mobile screen resize or load
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setPinned(false);
      }
    };
    window.addEventListener('resize', handleResize);
    // Run once on mount
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    tenantStorage.setItem(MENU_PINNED_KEY, pinned);
  }, [pinned]);

  return (
    <>
      <div className={`side-menu${pinned ? '' : ' side-menu-unpinned'}`}>
        <div
          className="menu-header"
          onClick={() => navigate('/')}
        >
          <span className="menu-header-icon">
            <MenuLogo />
          </span>
        </div>
        <div
          className="menu-pin"
          onClick={handlePinClick}
          title={pinned ? "Unpin menu" : "Pin menu"}
        >
          <FaThumbtack style={{ transform: pinned ? 'rotate(0deg)' : 'rotate(45deg)' }} />
        </div>
        <ul className="menu-list">
          {filteredMenuItems.map(item => (
            <li
              key={item.key}
              className={`menu-item${!item.enabled ? ' disabled' : ''}${path.startsWith(item.path) ? ' selected' : ''}`}
              title={item.label}
              onClick={() => handleMenuClick(item)}
            >
              <span className="menu-icon">{item.icon}</span>
              {pinned && <span className="menu-label">{item.label}</span>}
            </li>
          ))}
        </ul>
        {pinned && (
          <div className="menu-footer">
            <div className="menu-footer-content">
              <div className="menu-footer-links">
                <a href="/terms" className="menu-footer-link">Terms & Conditions</a>
                <span className="menu-footer-separator">•</span>
                <a href="/contact" className="menu-footer-link">Contact Us</a>
                <span className="menu-footer-separator">•</span>
                <a href="/about" className="menu-footer-link">About Us</a>
              </div>
              <div className="menu-footer-version">
                v{process.env.REACT_APP_VERSION || 'Test Version'}
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastConfirm
        show={showLogoutConfirm}
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </>
  );
};

export default Menu;